/**
 * Round-robin schedule generator
 * Generates all possible matchups for a league where everyone plays everyone
 */

/**
 * Generate round-robin schedule
 * @param {Array} participants - Array of participant objects with {userId, firstName, lastName}
 * @param {number} rounds - Number of times each team plays each other (1 = single round-robin, 2 = double round-robin)
 * @returns {Array} Array of match objects ready to be created
 */
export const generateRoundRobinSchedule = (participants, rounds = 1) => {
  if (!participants || participants.length < 2) {
    throw new Error('Need at least 2 participants to generate a schedule');
  }

  const matches = [];
  const participantCount = participants.length;

  // Generate matches for each round
  for (let roundNum = 1; roundNum <= rounds; roundNum++) {
    // Generate all possible pairings
    for (let i = 0; i < participantCount; i++) {
      for (let j = i + 1; j < participantCount; j++) {
        const participant1 = participants[i];
        const participant2 = participants[j];

        matches.push({
          participant1Id: participant1.userId,
          participant1Name: `${participant1.firstName} ${participant1.lastName}`,
          participant2Id: participant2.userId,
          participant2Name: `${participant2.firstName} ${participant2.lastName}`,
          participant1Score: null,
          participant2Score: null,
          winnerId: null,
          status: 'scheduled',
          scheduledTime: null,
          location: null
        });
      }
    }
  }

  return matches;
};

/**
 * Generate schedule with specific number of games per participant
 * @param {Array} participants - Array of participant objects
 * @param {number} gamesPerParticipant - Target number of games each participant should play
 * @returns {Array} Array of match objects
 */
export const generateScheduleByGames = (participants, gamesPerParticipant) => {
  if (!participants || participants.length < 2) {
    throw new Error('Need at least 2 participants to generate a schedule');
  }

  const participantCount = participants.length;
  const maxGamesPerParticipant = participantCount - 1; // Everyone plays everyone once

  if (gamesPerParticipant > maxGamesPerParticipant) {
    // If requested games exceed single round-robin, calculate needed rounds
    const rounds = Math.ceil(gamesPerParticipant / maxGamesPerParticipant);
    return generateRoundRobinSchedule(participants, rounds);
  }

  // Generate partial schedule
  const matches = [];
  const gameCount = new Map(participants.map(p => [p.userId, 0]));

  // Try to evenly distribute games
  for (let i = 0; i < participantCount; i++) {
    for (let j = i + 1; j < participantCount; j++) {
      const participant1 = participants[i];
      const participant2 = participants[j];

      // Check if both participants can play another game
      if (gameCount.get(participant1.userId) < gamesPerParticipant &&
          gameCount.get(participant2.userId) < gamesPerParticipant) {

        matches.push({
          participant1Id: participant1.userId,
          participant1Name: `${participant1.firstName} ${participant1.lastName}`,
          participant2Id: participant2.userId,
          participant2Name: `${participant2.firstName} ${participant2.lastName}`,
          participant1Score: null,
          participant2Score: null,
          winnerId: null,
          status: 'scheduled',
          scheduledTime: null,
          location: null
        });

        gameCount.set(participant1.userId, gameCount.get(participant1.userId) + 1);
        gameCount.set(participant2.userId, gameCount.get(participant2.userId) + 1);
      }
    }
  }

  return matches;
};

/**
 * Generate single-elimination bracket
 * @param {Array} participants - Array of participant objects
 * @param {boolean} randomize - Whether to randomize seeding
 * @returns {Array} Array of match objects for first round (subsequent rounds created as matches complete)
 */
export const generateEliminationBracket = (participants, randomize = false) => {
  if (!participants || participants.length < 2) {
    throw new Error('Need at least 2 participants to generate a bracket');
  }

  const participantCount = participants.length;

  // Check if power of 2
  const isPowerOf2 = (participantCount & (participantCount - 1)) === 0;

  if (!isPowerOf2) {
    console.warn('Participant count is not a power of 2. Some participants will have byes.');
  }

  // Shuffle participants if randomize is true
  let seededParticipants = [...participants];
  if (randomize) {
    seededParticipants = seededParticipants.sort(() => Math.random() - 0.5);
  }

  // Calculate number of first-round matches
  const nextPowerOf2 = Math.pow(2, Math.ceil(Math.log2(participantCount)));
  const byeCount = nextPowerOf2 - participantCount;
  const firstRoundMatchCount = Math.floor((participantCount - byeCount) / 2);

  const matches = [];
  let participantIndex = 0;

  // Create first round matches
  for (let i = 0; i < firstRoundMatchCount; i++) {
    const participant1 = seededParticipants[participantIndex++];
    const participant2 = seededParticipants[participantIndex++];

    matches.push({
      participant1Id: participant1.userId,
      participant1Name: `${participant1.firstName} ${participant1.lastName}`,
      participant2Id: participant2.userId,
      participant2Name: `${participant2.firstName} ${participant2.lastName}`,
      participant1Score: null,
      participant2Score: null,
      winnerId: null,
      round: 1,
      status: 'scheduled',
      scheduledTime: null,
      location: null
    });
  }

  // Add bye matches (participants who advance automatically)
  while (participantIndex < participantCount) {
    const participant = seededParticipants[participantIndex++];
    matches.push({
      participant1Id: participant.userId,
      participant1Name: `${participant.firstName} ${participant.lastName}`,
      participant2Id: null,
      participant2Name: 'BYE',
      participant1Score: null,
      participant2Score: null,
      winnerId: participant.userId,
      round: 1,
      status: 'completed',
      scheduledTime: null,
      location: null
    });
  }

  return matches;
};

/**
 * Calculate total number of matches for a round-robin tournament
 * @param {number} participantCount - Number of participants
 * @param {number} rounds - Number of rounds
 * @returns {number} Total number of matches
 */
export const calculateRoundRobinMatches = (participantCount, rounds = 1) => {
  if (participantCount < 2) return 0;
  return (participantCount * (participantCount - 1) / 2) * rounds;
};

/**
 * Calculate total number of matches for a single-elimination tournament
 * @param {number} participantCount - Number of participants
 * @returns {number} Total number of matches (including potential byes)
 */
export const calculateEliminationMatches = (participantCount) => {
  if (participantCount < 2) return 0;
  return participantCount - 1;
};

/**
 * Generate playoff bracket from standings
 * @param {Array} standings - Sorted array of standings (top performers first)
 * @param {number} playoffCount - Number of participants in playoffs (4, 8, 16, etc.)
 * @param {boolean} reseed - Whether to reseed after each round (higher seed always plays lower)
 * @returns {Array} Array of playoff match objects
 */
export const generatePlayoffBracket = (standings, playoffCount = 4, reseed = false) => {
  if (!standings || standings.length < 2) {
    throw new Error('Need at least 2 participants to generate playoffs');
  }

  if (playoffCount > standings.length) {
    playoffCount = standings.length;
  }

  // Ensure playoff count is power of 2
  const validPlayoffCounts = [2, 4, 8, 16, 32];
  if (!validPlayoffCounts.includes(playoffCount)) {
    throw new Error('Playoff count must be 2, 4, 8, 16, or 32');
  }

  // Get top performers
  const topPerformers = standings.slice(0, playoffCount);

  const matches = [];
  const roundCount = Math.log2(playoffCount);

  // Generate first round matchups using standard seeding
  // 1 vs 8, 4 vs 5, 2 vs 7, 3 vs 6 (for 8-team playoff)
  const firstRoundMatchups = [];
  for (let i = 0; i < playoffCount / 2; i++) {
    const highSeed = i;
    const lowSeed = playoffCount - 1 - i;
    firstRoundMatchups.push([topPerformers[highSeed], topPerformers[lowSeed]]);
  }

  // Create first round matches
  firstRoundMatchups.forEach(([participant1, participant2], index) => {
    matches.push({
      participant1Id: participant1.participantId,
      participant1Name: participant1.participantName,
      participant2Id: participant2.participantId,
      participant2Name: participant2.participantName,
      participant1Score: null,
      participant2Score: null,
      winnerId: null,
      round: 1,
      status: 'scheduled',
      scheduledTime: null,
      location: null,
      isPlayoff: true,
      matchupSeed: `${highSeed + 1} vs ${lowSeed + 1}`
    });
  });

  return matches;
};

/**
 * Get playoff format options based on participant count
 * @param {number} participantCount - Total number of participants in league
 * @returns {Array} Array of valid playoff format options
 */
export const getPlayoffFormatOptions = (participantCount) => {
  const options = [];

  if (participantCount >= 2) {
    options.push({ value: 2, label: 'Top 2 (Finals Only)', description: '1st vs 2nd seed' });
  }

  if (participantCount >= 4) {
    options.push({
      value: 4,
      label: 'Top 4 (Semi-Finals)',
      description: '1v4, 2v3 → Finals'
    });
  }

  if (participantCount >= 8) {
    options.push({
      value: 8,
      label: 'Top 8 (Quarter-Finals)',
      description: '1v8, 4v5, 2v7, 3v6 → Semi-Finals → Finals'
    });
  }

  if (participantCount >= 16) {
    options.push({
      value: 16,
      label: 'Top 16 (Sweet Sixteen)',
      description: 'Full 16-team playoff bracket'
    });
  }

  return options;
};

/**
 * Validate if league is ready for playoffs
 * @param {Array} standings - Current standings
 * @param {Array} matches - All league matches
 * @returns {Object} Validation result with isReady boolean and message
 */
export const validatePlayoffReadiness = (standings, matches) => {
  if (!standings || standings.length < 2) {
    return {
      isReady: false,
      message: 'Need at least 2 participants with standings to generate playoffs'
    };
  }

  // Check if regular season is complete
  const scheduledMatches = matches.filter(m => !m.isPlayoff);
  const completedRegularSeason = scheduledMatches.every(m => m.status === 'completed');

  if (!completedRegularSeason && scheduledMatches.length > 0) {
    const remaining = scheduledMatches.filter(m => m.status !== 'completed').length;
    return {
      isReady: false,
      message: `Complete all ${remaining} regular season matches before generating playoffs`
    };
  }

  // Check if standings have been properly calculated
  const hasValidStandings = standings.every(s =>
    typeof s.wins === 'number' &&
    typeof s.losses === 'number' &&
    s.participantId &&
    s.participantName
  );

  if (!hasValidStandings) {
    return {
      isReady: false,
      message: 'Standings data is incomplete. Ensure all matches have been scored.'
    };
  }

  return {
    isReady: true,
    message: 'Ready to generate playoffs',
    participantCount: standings.length
  };
};

/**
 * Check if a round is complete and generate next round matches if needed
 * @param {Array} allMatches - All matches in the tournament
 * @param {Object} completedMatch - The match that was just completed
 * @returns {Array|null} Array of next round matches to create, or null if round not complete
 */
export const generateNextRoundMatches = (allMatches, completedMatch) => {
  const currentRound = completedMatch.round;

  // Get all matches in the current round
  const currentRoundMatches = allMatches.filter(m => m.round === currentRound);

  // Check if all matches in current round are completed
  const allComplete = currentRoundMatches.every(m => m.status === 'completed');

  if (!allComplete) {
    return null; // Round not yet complete
  }

  // Get winners from current round, maintaining bracket order
  const winners = currentRoundMatches
    .sort((a, b) => {
      // Sort by match creation order or ID to maintain bracket structure
      return (a.id || '').localeCompare(b.id || '');
    })
    .map(match => {
      const winnerIsParticipant1 = match.winnerId === match.participant1Id;
      const winnerName = winnerIsParticipant1 ? match.participant1Name : match.participant2Name;

      return {
        userId: match.winnerId,
        name: winnerName
      };
    });

  // If only 1 winner, tournament is complete
  if (winners.length === 1) {
    return null;
  }

  // Generate next round matches by pairing winners sequentially
  const nextRoundMatches = [];
  for (let i = 0; i < winners.length; i += 2) {
    if (i + 1 < winners.length) {
      nextRoundMatches.push({
        participant1Id: winners[i].userId,
        participant1Name: winners[i].name,
        participant2Id: winners[i + 1].userId,
        participant2Name: winners[i + 1].name,
        participant1Score: null,
        participant2Score: null,
        winnerId: null,
        round: currentRound + 1,
        status: 'scheduled',
        scheduledTime: null,
        location: null
      });
    }
  }

  return nextRoundMatches.length > 0 ? nextRoundMatches : null;
};
